///
/// EarthBrowser
///
/// Error Handling Utilities
/// Copyright (c) 2025 Geodata Labs
///

import type { SerializedError } from "@reduxjs/toolkit"
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query"

/**
 * Type guard to check if error is a FetchBaseQueryError
 */
function isFetchBaseQueryError(error: unknown): error is FetchBaseQueryError {
	return typeof error === "object" && error !== null && "status" in error
}

/**
 * Type guard to check if error is a SerializedError
 */
function isSerializedError(error: unknown): error is SerializedError {
	return typeof error === "object" && error !== null && "message" in error
}

/**
 * Type guard to check if error has data property with message
 */
function hasDataMessage(error: unknown): error is { data: { message: string } } {
	return (
		typeof error === "object" &&
		error !== null &&
		"data" in error &&
		typeof (error as Record<string, unknown>).data === "object" &&
		(error as Record<string, unknown>).data !== null &&
		"message" in ((error as Record<string, unknown>).data as Record<string, unknown>) &&
		typeof ((error as Record<string, unknown>).data as Record<string, unknown>).message === "string"
	)
}

/**
 * Type guard to check if error has data as string
 */
function hasDataString(error: unknown): error is { data: string } {
	return (
		typeof error === "object" &&
		error !== null &&
		"data" in error &&
		typeof (error as Record<string, unknown>).data === "string"
	)
}

/**
 * Extract user-friendly error message from RTK Query error
 *
 * Handles various error structures from:
 * - Hono backend (HTTPException)
 * - Network errors
 * - Validation errors
 * - Generic errors
 */
export function extractErrorMessage(
	error: FetchBaseQueryError | SerializedError,
	fallbackMessage: string = "An error occurred"
): string {
	// Log for debugging
	console.error("Error object:", error)

	// Handle null/undefined
	if (!error) {
		return fallbackMessage
	}

	// 1. RTK Query FetchBaseQueryError with data.message (most common from Hono)
	if (hasDataMessage(error)) {
		return error.data.message
	}

	// 2. RTK Query with data.error (alternative backend format)
	if (
		typeof error === "object" &&
		error !== null &&
		"data" in error &&
		typeof (error as Record<string, unknown>).data === "object" &&
		(error as Record<string, unknown>).data !== null &&
		"error" in ((error as Record<string, unknown>).data as Record<string, unknown>) &&
		typeof ((error as Record<string, unknown>).data as Record<string, unknown>).error === "string"
	) {
		return ((error as Record<string, unknown>).data as Record<string, unknown>).error as string
	}

	// 3. RTK Query with data as string (sometimes Hono sends plain text)
	if (hasDataString(error)) {
		return error.data
	}

	// 4. SerializedError with message
	if (isSerializedError(error) && error.message) {
		return error.message
	}

	// 5. Error object with error property
	if (
		typeof error === "object" &&
		error !== null &&
		"error" in error &&
		typeof (error as Record<string, unknown>).error === "string"
	) {
		return (error as Record<string, unknown>).error as string
	}

	// 6. HTTP status with generic message
	if (isFetchBaseQueryError(error)) {
		const statusMessages: Record<number, string> = {
			400: "Invalid request",
			401: "Unauthorized",
			403: "Forbidden",
			404: "Not found",
			409: "Conflict - resource already exists",
			429: "Too many requests - please try again later",
			500: "Server error",
			503: "Service unavailable",
		}

		if (typeof error.status === "number" && error.status in statusMessages) {
			return statusMessages[error.status]
		}

		if (typeof error.status === "number") {
			return `Error ${error.status}`
		}
	}

	// 7. Network error
	if (
		typeof error === "object" &&
		error !== null &&
		"name" in error &&
		typeof (error as Record<string, unknown>).name === "string" &&
		((error as Record<string, unknown>).name === "FetchError" ||
			(error as Record<string, unknown>).name === "NetworkError")
	) {
		return "Network error - please check your connection"
	}

	// Fallback
	return fallbackMessage
}

/**
 * Create a user-friendly error toast message
 */
export function formatErrorForToast(
	error: FetchBaseQueryError | SerializedError,
	context: string = "Operation"
): { title: string; description: string } {
	const message = extractErrorMessage(error, `${context} failed`)

	return {
		title: context,
		description: message,
	}
}
